export const cdtStudioEn = {
  pageTitle: "CDT Studio",
  loading: "Loading base planogram...",
  loadFailed: "Failed to load the base planogram.",
  inProgress: "The base planogram is still being generated. Try again in a moment.",
  itemsSummary: "{{skus}} · {{bays}}",
  itemsSkus_one: "1 SKU",
  itemsSkus_other: "{{count}} SKUs",
  itemsBays_one: "1 bay",
  itemsBays_other: "{{count}} bays",
  placementRule: {
    chip: "Placement rule",
    where_one: "Observed on shelf {{shelves}}",
    where_other: "Observed on shelves {{shelves}}",
    onBays_one: "bay {{bays}}",
    onBays_other: "bays {{bays}}",
    /** A run of shelves or bays: "2 to 7". */
    range: "{{from}} to {{to}}",
    title_one: "Keep {{variant}} on shelf {{shelves}}?",
    title_other: "Keep {{variant}} on shelves {{shelves}}?",
    detail:
      "The selected grouping may place some of these products on other shelves when the layout " +
      "is regenerated. Saving this rule keeps them on the shelves shown, forming a clean block " +
      "where the fixture allows.",
    // The tree DOES hold this band, so the first sentence would be false. The
    // second one is not: the tree binds the studio, the rule binds the pogger.
    detailTreeHolds:
      "Saving this rule keeps these products on the shelves shown when planograms are generated, " +
      "forming a clean block where the fixture allows.",
    cost_one:
      "Covers 1 shelf location. Width and facing allocation stay flexible, subject to other " +
      "constraints. Full detail on the finalize receipt.",
    cost_other:
      "Covers {{cells}} shelf locations. Width and facing allocation stay flexible, subject to " +
      "other constraints. Full detail on the finalize receipt.",
    promotionNote:
      'To apply this rule separately, "{{variant}}" becomes its own product group within {{parent}}.',
    keep: "Use this rule",
    decline: "Leave placement flexible",
    /* NOT "Undo". This reopens THIS rule's keep/decline question, and it sits
       in the findings band beside the exception rows — so a second control
       called Undo stood next to the work, hard-wired to the restriction
       decision no matter what the user did after it. Owner: "undo button in cdt
       studio doesn't undo the latest, it undoes the variant / restrictions...
       not the exception." The toolbar's Undo is the one that undoes the latest,
       and it is now the only thing wearing that word. */
    changeAnswer: "Change answer",
    keptChip: "Selected — saved when you finish",
    declinedChip: "Placement left flexible",
    /* A KEPT rule leaves the findings list (owner, 2026-09-02: "after the
       placement rules are accepted, they should go somewhere else ... they
       take up too much of the screen and make looking at exceptions too
       hard") and collapses into this compact pill; expanding it keeps the
       Change answer / Leave placement flexible flow reachable. Display relocation only —
       the kept snapshot still writes on finalize. */
    keptSummary_one: "1 placement rule selected — saved when you finish",
    keptSummary_other: "{{count}} placement rules selected — saved when you finish",
    /* The shelf bands, asked as ONE card (Dana, 2026-10-30): one row per band, a tick each,
       one button that keeps the ticked ones and leaves the rest flexible. */
    bandsTitle_one: "Keep the shelf band the base shows?",
    bandsTitle_other: "Keep the {{count}} shelf bands the base shows?",
    bandsIntro_one:
      "Studio found 1 shelf band on this wall, {{sections}}: inside each brand column one product form keeps to the same shelves (shelf 1 is the top).",
    bandsIntro_other:
      "Studio found {{count}} shelf bands on this wall, {{sections}}: inside each brand column one product form keeps to the same shelves (shelf 1 is the top).",
    bandsIntroNoSections_one:
      "Studio found 1 shelf band on this wall: inside each brand column one product form keeps to the same shelves (shelf 1 is the top).",
    bandsIntroNoSections_other:
      "Studio found {{count}} shelf bands on this wall: inside each brand column one product form keeps to the same shelves (shelf 1 is the top).",
    bandsIntroWholeBlocks_one:
      "Studio found 1 shelf band on this wall: it is a block that keeps to the same shelves (shelf 1 is the top).",
    bandsIntroWholeBlocks_other:
      "Studio found {{count}} shelf bands on this wall: each is a block that keeps to the same shelves (shelf 1 is the top).",
    /** A band that is a whole block, not one product form inside it: "Gain · whole block". */
    wholeBlock: "whole block",
    bandsInSection: "{{count}} in {{section}}",
    bandsExplain:
      "A ticked shelf band is held in every store planogram, even where a store has fewer shelves (the bands keep their order top to bottom). An unticked shelf band may land on any shelf when a store is tight.",
    bandsColumnBand: "Shelf band",
    bandsColumnStays: "Stays on",
    bandsColumnAcross: "Across",
    staysOn_one: "shelf {{shelves}}",
    staysOn_other: "shelves {{shelves}}",
    bandsKeep_one: "Keep this 1",
    bandsKeep_other: "Keep these {{count}}",
    bandsKeepNone: "Keep none",
    bandsKeepNote: "(the count follows your ticks; the bands count as answered once you press it)",
    showOnWall: "Show me on the wall",
    /* The bands answered: the card folds to one line in its place. */
    bandsKept_one: "1 shelf band kept",
    bandsKept_other: "{{count}} shelf bands kept",
    bandsFlexible_one: "1 left flexible",
    bandsFlexible_other: "{{count}} left flexible",
    bandsChange: "Change",
    bandsDeclined_one: "1 shelf band may land on any shelf when a store is tight",
    bandsDeclined_other: "{{count}} shelf bands may land on any shelf when a store is tight",
  },
  setup: {
    title: "CDT Studio configuration",
    intro:
      "Set the naming profile and inference rules used whenever CDT Studio opens. Product attributes come from the live category API.",
    summary: "{{attributes}} attributes",
    summaryPlural: "{{attributes}} attributes",
    sections: {
      basic: "Profile and source columns",
      attributes: "Attribute mapping and order",
      semantic: "Semantic roles and inference",
      normalization: "Quantity normalization",
      hierarchy: "Hierarchy moves",
      vertical: "Vertical blocking",
      aesthetics: "Aesthetics",
      advanced: "Advanced profile editor",
    },
    actions: {
      close: "Close",
      reset: "Reset",
      save: "Save configuration",
      addAttribute: "Add attribute",
      addTier: "Add tier",
      remove: "Remove",
      moveUp: "Move up",
      moveDown: "Move down",
    },
    fields: {
      merchant: "Merchant",
      category: "Category",
      profileVersion: "Profile version",
      itemIdentifier: "POG item identifier",
      skuColumn: "SKU column",
      nameColumn: "Name column",
      attributeKey: "Key",
      attributeLabel: "Label",
      sourceColumn: "Source column",
      selectColumn: "Select CSV column",
      pogField: "POG field",
      notMapped: "Not mapped — user fills",
      missingValues: "Missing values",
      blockIdentity: "Block identity order",
      verticalRoles: "Vertical role order",
      brandRole: "Brand role",
      tierRole: "Tier role",
      quantityRole: "Quantity role",
      terminalVariantRole: "Terminal variant role",
      useFirstRegex: "Use first-value regex",
      firstRegex: "First-value regex",
      useLastRegex: "Use last-value regex",
      lastRegex: "Last-value regex",
      normalizationEnabled: "Enable quantity normalization",
      inferFromName: "Infer quantity from product name",
      zeroIsMissing: "Treat zero as missing",
      liquidOuncesAreFluid: "Treat liquid ounces as fluid ounces",
      unitPreference: "Unit preference order",
      liquidNameTerms: "Liquid product-name terms",
      minimumFamilyEvidence: "Minimum family evidence",
      reconciliationMinimum: "Reconciliation ratio minimum",
      reconciliationMaximum: "Reconciliation ratio maximum",
      minFacingsPerShelf: "Minimum facings per shelf",
      roundTripFloorShare: "Round-trip floor share",
      fillFloorShare: "Fill floor share",
      fillDropTolerancePts: "Fill drop tolerance (points)",
      seamPass: "Seam pass",
      blockShare: "Block shelf share",
      fragmentJoinPct: "Fragment join (% of shelf)",
      minFillRatio: "Minimum fill ratio",
      preserveRange: "Preserve ranged products",
      onlyIfBetter: "Apply only when score improves",
      brandAttribute: "Brand attribute",
      tierAttribute: "Tier attribute",
      sizeAttribute: "Quantity attribute",
      flavourAttribute: "Terminal variant attribute",
      verticalAttributes: "Vertical attributes",
      tierLabel: "Tier label",
      tierValues: "Tier values",
      lastInBlock: "Values placed last in a block",
      bayOwners: "Whole-bay owners",
      bulkPounds: "Bulk threshold (lb)",
      lowerShelfShare: "Lower-shelf share",
      jitterPacks: "Edge jitter allowance (packs)",
      raggedZeroPacks: "Zero-score raggedness (packs)",
      flagsPerHundredZero: "Zero-score flags per 100 SKUs",
      blockingPenalty: "Blocking severity penalty",
      toCheckPenalty: "To-check severity penalty",
      toReviewPenalty: "To-review severity penalty",
      blockingWeight: "Blocking weight",
      cdtWeight: "CDT weight",
      fullnessWeight: "Fullness weight",
      ergonomicsWeight: "Ergonomics weight",
      rulesKeptWeight: "Shelf rules weight",
      rangeWeight: "Range kept weight",
      shapeSplit: "Block shape split",
      edgesSplit: "Straight edges split",
      ladderSplit: "Tier ladder split",
      detectorSplit: "Detector CDT split",
      orderingSplit: "Ordering CDT split",
      fullProfileJson: "Full profile JSON",
    },
    tooltips: {
      merchant: "Metadata only. Names the merchant that owns this saved category profile.",
      category: "Metadata only. Names the category configuration; it does not rank the CDT.",
      profileVersion: "Metadata identifying this profile revision; it does not change inference.",
      itemIdentifier:
        "Selects the POG field matched to item-master rows. A wrong choice leaves products missing or mismatched before inference.",
      skuColumn: "CSV column containing the identifier used to join item-master rows to products.",
      nameColumn:
        "CSV product-name column. Names are displayed and may supply quantity evidence when name inference is enabled.",
      attributeKey:
        "Stable engine-facing attribute name used by inference, roles, rules, and overlays.",
      attributeLabel: "Display label for the hierarchy level. It does not change inference.",
      sourceColumn: "CSV column whose values feed this inference attribute.",
      pogField:
        "Optional POG field used to prefill missing item-master rows. It does not change existing rows.",
      missingValues:
        "These case-insensitive spellings become Unspecified and do not create meaningful hierarchy branches.",
      blockIdentity:
        "Resolver only. Major-to-minor attributes used to scope split and orphan audits; this does not reorder initial inference.",
      verticalRoles:
        "Scoring and repair. Ordered attributes that define a vertical merchandising block; this does not select the initial spine.",
      brandRole:
        "Scoring and diagnostics. Attribute used for brand grouping and elimination checks.",
      tierRole:
        "Scoring and diagnostics. Attribute whose configured tier values are checked as horizontal bands.",
      quantityRole:
        "Normalization and resolver. Attribute treated as quantity/size for family parsing and size-order checks.",
      terminalVariantRole:
        "Resolver and scoring. Attribute used for terminal-variant ordering and last-value behavior.",
      useFirstRegex:
        "Enables the saved first-value regex. Disabled means no implicit Original or Regular rule is applied.",
      firstRegex:
        "Inference tie-break. Matching values move first only where shelf evidence states no order.",
      useLastRegex:
        "Enables the saved last-value regex. Disabled means no implicit sensitive or Free & Clear rule is applied.",
      lastRegex:
        "Value ordering. Matching values move to the end; this does not select the attribute's spine level.",
      normalizationEnabled:
        "Enables quantity parsing, name enrichment, and family reconciliation for the quantity role.",
      inferFromName:
        "Uses confirmed quantities in product names to fill missing values or complete bare numbers.",
      zeroIsMissing:
        "Treats numeric zero as missing and eligible for name-based quantity recovery.",
      liquidOuncesAreFluid:
        "Treats ambiguous ounces for liquid products as fluid ounces, keeping volume separate from mass.",
      unitPreference:
        "First matching unit wins when a product name contains several quantities; no hidden unit order is added.",
      liquidNameTerms:
        "Name terms that identify liquids when deciding whether ounces mean fluid ounces.",
      minimumFamilyEvidence:
        "Unambiguous values required before an ambiguous quantity may join a unit family. Higher is more conservative.",
      reconciliationMinimum:
        "Lower bound of the inclusive ratio window for joining ambiguous values to a volume family.",
      reconciliationMaximum:
        "Upper bound of the inclusive ratio window for joining ambiguous values to a volume family.",
      minFacingsPerShelf:
        "Hierarchy moves. Facings per shelf required to treat a moved value as a full-height vertical block.",
      roundTripFloorShare:
        "Hierarchy moves. Minimum shelf-exact reproduction share required after regenerating the changed hierarchy.",
      fillFloorShare:
        "Hierarchy moves. Minimum fill share targeted per run when closing gaps after a move.",
      fillDropTolerancePts:
        "Hierarchy moves. Fill percentage points a move may lose against its before-state.",
      seamPass:
        "Post-move repair: off skips seam squaring, facings-neutral allows it without facing changes, and on allows the full pass.",
      blockShare:
        "Resolver threshold. Shelf coverage required before a value counts as a vertical block; higher requires more coverage.",
      fragmentJoinPct:
        "Direct inference setting. Largest gap along a shelf still treated as one fragment, as a percentage of the bay's shelf width; higher values hide more small gaps.",
      minFillRatio:
        "Repair only. The vertical-blocking fix refuses to read seams from a plan below this fill ratio.",
      preserveRange:
        "Repair only. Prevents vertical-blocking repair from removing the last facing of a SKU.",
      onlyIfBetter:
        "Repair only. Keeps a vertical-blocking fix only when the composite aesthetics score improves.",
      brandAttribute:
        "Same setting as Brand role. Groups vertical blocking and brand-elimination checks.",
      tierAttribute:
        "Same setting as Tier role. Values are graded through the configured tier order; no order means the measure stands down.",
      sizeAttribute:
        "Same setting as Quantity role. Supplies size-order checks and derived bulk weight.",
      flavourAttribute:
        "Same setting as Terminal variant role. Supplies recurring variant and last-value checks.",
      verticalAttributes:
        "Same setting as Vertical role order. Defines the attribute path grouped as a vertical block.",
      tierLabel:
        "Display name for this tier. Row position, not the label text, defines top-to-bottom order.",
      tierValues:
        "Case-insensitive values belonging to this tier. Unlisted values are not graded as a tier.",
      lastInBlock:
        "Ordering score. Values or names containing these terms should close their block; no list means no implicit rule.",
      bayOwners:
        "Blocking score. Matching blocks must also align their edges with whole-bay boundaries.",
      bulkPounds:
        "Ergonomics score. Minimum derived pack weight considered bulk; 0 disables bulk grading.",
      lowerShelfShare:
        "Ergonomics score. Fraction of shelves, counted from the bottom, treated as the bulk lift zone.",
      jitterPacks:
        "Blocking score. Pack-widths of edge mismatch forgiven before vertical raggedness is penalized.",
      raggedZeroPacks:
        "Blocking score. Excess raggedness in pack-widths at which a vertical block scores zero.",
      flagsPerHundredZero:
        "CDT detector score. Weighted flags per 100 SKUs at which the detector score reaches zero.",
      blockingPenalty: "Detector score penalty assigned to each blocking-severity flag.",
      toCheckPenalty: "Detector score penalty assigned to each to-check flag.",
      toReviewPenalty: "Detector score penalty assigned to each to-review flag.",
      blockingWeight:
        "Overall-score share assigned to blocking. All component weights must total 100.",
      cdtWeight:
        "Overall-score share assigned to CDT compliance. All component weights must total 100.",
      fullnessWeight:
        "Overall-score share assigned to shelf gaps and overhang. All component weights must total 100.",
      ergonomicsWeight:
        "Overall-score share assigned to big-under-small and heavy-low placement. All component weights must total 100.",
      rulesKeptWeight:
        "Overall-score share assigned to obeying the accepted shelf rules. All component weights must total 100.",
      rangeWeight:
        "Overall-score share assigned to placing the whole range and keeping every brand. All component weights must total 100.",
      shapeSplit:
        "Share of the blocking component assigned to block shape: each block one piece that fills its rectangle. Shape, straight edges and tier ladder must total 1.",
      edgesSplit:
        "Share of the blocking component assigned to straight edges: stripes on their left and right edges, bands on their top and bottom. Shape, straight edges and tier ladder must total 1.",
      ladderSplit:
        "Share of the blocking component assigned to the tier ladder, when one is configured. Shape, straight edges and tier ladder must total 1.",
      detectorSplit:
        "Share of the CDT component assigned to detector flags; detector and ordering must total 1.",
      orderingSplit:
        "Share of the CDT component assigned to ordering; detector and ordering must total 1.",
      fullProfileJson:
        "Complete validated profile, including JSON-only mappings and rules. Friendly-field edits rewrite this text.",
    },
    advancedReference: {
      title: "JSON-only properties",
      sectionSemantics:
        "Per-section column remapping, conditions, and dropped levels. First matching mapping wins.",
      overlayAttr: "Live attribute represented by a profile level.",
      unitAliases: "Case-insensitive source unit spellings mapped to canonical units.",
      reconciliationBlockAttributes:
        "Attributes defining quantity-evidence groups; false disables reconciliation.",
      firstValueRules: "Per-attribute substrings preferred at the start of sibling value order.",
      lastValueRules:
        "Per-attribute substrings preferred at the end; the terminal rule also feeds last-in-block scoring.",
    },
    help: {
      attributes:
        "Order here is the saved candidate preference used by inference; shelf geometry still decides the hierarchy.",
      commaSeparated: "Separate values with commas; their order is preserved.",
      ignoredAttributes: "Never a CDT level: {{attributes}}",
      advanced:
        "Every profile value is available here, including value rules and unit aliases. Valid profile JSON updates the friendly fields; loading always validates and compiles the complete profile.",
    },
    errors: {
      saveFailed: "Could not save the configuration. Please try again.",
      title: "Fix these issues before loading",
      invalidJson: "The full profile editor does not contain valid JSON.",
      persistence: "Could not save the configuration in this browser. Check available storage.",
    },
    saved: "Configuration saved for this category.",
    fragmentJoinLegacy: "Legacy value {{value}}, not converted",
  },
  configuration: {
    settingsLoadFailed: "Could not load merchant settings. Please try again.",
    accessDenied: "You do not have permission to edit CDT configuration.",
    archivedCategory: "Archived categories cannot activate CDT Studio configuration.",
    invalidCategory: "Invalid category.",
    loadFailed: "Could not load this category.",
    loading: "Loading CDT Studio configuration…",
    defaultProfile:
      "Starting from the default profile for {{merchant}}. Check the attribute mapping before saving.",
  },
  /** The list's counts, in the words its cards use. */
  severityCount: {
    blocking_one: "1 Issue",
    blocking_other: "{{count}} Issues",
    toCheck_one: "1 Check",
    toCheck_other: "{{count}} Checks",
    toReview_one: "1 Review",
    toReview_other: "{{count}} Reviews",
  },
  /** A card's chip, in the words the counts use. */
  severityChip: {
    blocking: "Issue",
    toCheck: "Check",
    toReview: "Review",
  },
  toolbar: {
    undo: "Undo",
    redo: "Redo",
    fixBlocking: "Fix vertical blocking",
    fixAll: "Fix all",
    fixAllStarting: "Fixing…",
    fixAllProgress_one: "Fixing… 1 fix tried",
    fixAllProgress_other: "Fixing… {{count}} fixes tried",
    weighingMove: "Weighing the move…",
    waitForFixAll: "Wait for Fix all to finish",
    waitForMove: "Wait until the move is weighed",
    finalize: "Finalize CDTs",
    downloadCsv: "Save CDTs to file",
    more: "More",
  },
  /** The panel's foot: the one primary action, and what it waits on. */
  footer: {
    finalize: "Finalize",
    waiting_one: "Waiting on 1 answer",
    waiting_other: "Waiting on {{count}} answers",
    waitingThen:
      "Then Finalize writes the rules every store planogram is built from; store planograms can't be generated before that.",
    layoutOf: "{{brand}}'s layout",
    splitOf: "the {{name}} split",
    bands_one: "the shelf band",
    bands_other: "the {{count}} shelf bands",
    sizeTags_one: "the size tag",
    sizeTags_other: "the {{count}} size tags",
    ready: "Ready to finalize",
    ships: "Ships {{sections}}, {{brands}} and {{bands}}.",
    /** A wall with no brand level: no brand blocks to count. */
    shipsNoBrands: "Ships {{sections}} and {{bands}}.",
    sections_one: "1 section",
    sections_other: "{{count}} sections",
    brandBlocks_one: "1 brand block",
    brandBlocks_other: "{{count}} brand blocks",
    shelfBands_one: "1 shelf band",
    shelfBands_other: "{{count}} shelf bands",
    finalized: "Finalized",
    finalizedDetail: "Store planograms can now be generated from the rules the Studio shipped.",
  },
  provenance: {
    noNewBreaks: "No new splits — your changes broke nothing.",
    newBreaks: "{{count}} blocks newly broken by your changes",
    regenDelta:
      "Regeneration reproduces {{exact}}/{{total}} products shelf-exact — exceptions on this preview can be regeneration artefacts, not your changes",
    regenShed: "the regenerated plan carries {{count}} shed facing(s)",
    regenOverflow:
      "{{lanes}} shelf(s) hold {{length}} more than their run — packs compress and can overlap",
    moved: "{{count}} SKUs moved vs base",
  },
  evidence: {
    built: "Built: {{order}}",
    want: "Want: {{order}}",
    fix: "Fix",
    acceptNamesVariant:
      "Keeps {{variant}} on these shelves as a placement rule for the stores of this flow.",
    acceptNoRow:
      "Keeps this difference as it is. Its products sit in more than one section, so no placement rule is saved.",
    accept: "Keep this intentional difference",
    dismiss: "Dismiss",
  },
  resolver: {
    blockingTooSparse:
      "Vertical blocking not applied: shelves are only {{fill}}% full. Fill the plan first (or regenerate), then try again.",
    blockingFixed: "Vertical blocking: score {{scoreBefore}} → {{scoreAfter}}.",
    blockingFixedBeforeFixes:
      "Vertical blocking: score {{scoreBefore}} → {{scoreAfter}}. Squared the blocks first, then fixed the cards again.",
    blockingFixedAdded_one: "1 facing added",
    blockingFixedAdded_other: "{{count}} facings added",
    blockingFixedRemoved_one: "1 facing removed",
    blockingFixedRemoved_other: "{{count}} facings removed",
    blockingFixedTraded: "{{added}} and {{removed}}",
    blockingFixedOn_one: "{{change}} on 1 product",
    blockingFixedOn_other: "{{change}} on {{count}} products",
    blockingFixedDropped_one: "1 product dropped",
    blockingFixedDropped_other: "{{count}} products dropped",
    blockingFixedRagged: "{{ragged}} of uneven edge left",
    blockingFixedResiduals_one: "1 block still out of line",
    blockingFixedResiduals_other: "{{count}} blocks still out of line",
    blockingHarms:
      "Vertical blocking not applied: it would split a block or fail a check the plan passes now. Nothing moved.",
    blockingNoGain:
      "Vertical blocking not applied: it would score {{scoreAfter}} against {{scoreBefore}} now. Nothing moved.",
    blockingNoGainSameScore:
      "Vertical blocking not applied: it would leave the score at {{scoreBefore}}. Nothing moved.",
    blockingUnscored:
      "Vertical blocking not applied: the plan it would make cannot be scored. Nothing moved.",
    blockingBelowDos:
      "Vertical blocking not applied: it would take a product below its days of supply. Nothing moved.",
    blockingTooTall:
      "Vertical blocking not applied: it would stand a pack on a shelf without the height for it. Nothing moved.",
    cannotRepairInPlace: "Can't auto-fix",
    cannotRepairInPlaceDetail: "No single fix for this card raises the shown score.",
    cannotRepairInPlaceDetailUnscored: "No fix clears this card. Keep it or dismiss it.",
    cannotRepairBelowDos: "No fix without taking a product below its days of supply.",
    fixAllDone_one: "Fix all: score {{scoreBefore}} → {{scoreAfter}}, 1 fix: {{fixes}}.",
    fixAllDone_other:
      "Fix all: score {{scoreBefore}} → {{scoreAfter}}, {{count}} fixes: {{fixes}}.",
    fixAllDoneRepeats_one:
      "Fix all: score {{scoreBefore}} → {{scoreAfter}}, {{count}} fixes, all the same: {{fixes}}.",
    fixAllDoneRepeats_other:
      "Fix all: score {{scoreBefore}} → {{scoreAfter}}, {{count}} fixes, {{distinct}} different: {{fixes}}.",
    fixAllMore: "{{names}} and {{count}} more",
    fixAllGathered: "{{block}}: {{count}} blocks gathered",
    flowSort: "Flow sort",
    flowCardTitle: "{{block}} out of order",
    flowCardDetail: "Its products don't follow the agreed order along the shelf. Sort them.",
    piecesCardTitle: "{{block}} split into {{count}} blocks",
    piecesCardDetail_one:
      "Part of the block stands apart on shelf {{shelves}}. Move it beside the rest so it reads as one block.",
    piecesCardDetail_other:
      "Part of the block stands apart on shelves {{shelves}}. Move it beside the rest so it reads as one block.",
    fillShelves: "Fill shelves",
    fillCardTitle_one: "Shelf {{shelves}} has gaps",
    fillCardTitle_other: "Shelves {{shelves}} have gaps",
    fillCardDetail_fixable:
      "The products on it don't reach from end to end. Add facings to the products already there.",
    fillCardDetail_maxFacings:
      "Every product on it already has its most facings. Add products to fill it.",
    fillCardDetail_noRoom:
      "The gap left is narrower than any product on it. Add a narrower product to fill it.",
    fillCardDetail_harm: "More facings here would lower the score. Fill it by hand.",
    fixAllWholePlan: "the plan",
    fixAllNoGain: "Fix all: nothing left that raises the score ({{score}}). Nothing moved.",
    fixAllFailed: "Fix all could not run: {{reason}}",
    treeEditHarms:
      "Tree not changed: the plan built from the tree would split a block or fail a check the plan passes now.",
    treeEditUnscored: "Tree not changed: the plan built from the tree cannot be scored.",
    treeEditNotMade:
      "Tree not changed: it is already in that place, so the order on the shelf would stay the same.",
    autoResolve: "Auto-fix",
    resetFixes: "Reset fixes",
    repositioned: "{{count}} facings moved vs base",
    regenerated:
      "Plan rebuilt with every block in flow; remaining issues are measured on the rebuilt plan",
    auditStrip:
      "sizes reversed {{inverted}} · blocks broken on a shelf {{laneBreaks}} · split blocks {{splits}}",
    gradeTitle: "Size order · every block",
    gradeSkipped: "{{node}} — {{why}}",
    gradePinned: "{{node}}: {{id}} held high — too tall for the shelf below",
    suggested: "Fix:",
    suggestion: {
      relocate: "Move these products into their block; facings may change.",
      resequence: "Reorder products to match the flow.",
      retag: "Fix the product grouping; nothing moves on the shelf.",
    },
  },
  /** The finding cards' words (`resolver/detectExceptions.ts`); a flag's English title and
   *  detail are made from these, so a change here changes both. */
  flagWords: {
    and: " and ",
    shelves_one: "shelf {{shelves}}",
    shelves_other: "shelves {{shelves}}",
    shelfRange: "shelves {{from}}–{{to}}",
    whereShelf: "shelf {{shelf}}",
    whereAisle: "another aisle, shelf {{shelf}}",
    thisPlan: "This plan",
    thisBlock: "this block",
    splitTitle: "{{block}} split into {{count}} blocks",
    splitDetail: "Other products break up this block. Move them out so it reads as one block.",
    bandHoleTitle: "{{block}} skips {{holes}}",
    bandHoleDetail:
      "On {{occupied}} but missing from {{holes}}. Stack it on adjacent shelves as one block.",
    scatterTitle: "{{block}} in {{count}} places",
    scatterDetail:
      "Its facings are spread out with other products in between. Move them together into one block.",
    contiguityTitle_one: "{{blocks}} splits the {{block}} block",
    contiguityTitle_other: "{{blocks}} split the {{block}} block",
    contiguityDetail_one:
      "1 facing from other blocks sits inside this block on {{shelves}}. Move it out.",
    contiguityDetail_other:
      "{{count}} facings from other blocks sit inside this block on {{shelves}}. Move them out.",
    fragmentTitle_one: "{{block}}: 1 facing away from its block ({{where}})",
    fragmentTitle_other: "{{block}}: {{count}} facings away from its block ({{where}})",
    fragmentDetail_one:
      "The block is on {{span}}; 1 facing sits apart on {{where}}. Move it into the block.",
    fragmentDetail_other:
      "The block is on {{span}}; {{count}} facings sit apart on {{where}}. Move them into the block.",
    wrapTitle: "{{lower}} wraps around {{upper}} ({{value}})",
    wrapDetail:
      "{{lower}} sits beside and under {{upper}}, often done on purpose to give a smaller brand a corner. Keep it if intended.",
    columnTitle: "{{value}} split between {{upper}} and {{lower}}",
    columnDetail:
      "{{value}} is on {{upperShelves}} and again on {{lowerShelves}}, with {{between}} in between. Move the lower {{value}} up beside the rest.",
    blockOnShelves: "{{block}} → {{shelves}}",
    wrapWant: "{{value}} as it is, if the wrap is intended",
    columnWant: "{{value}} in one block",
    orphanAisleTitle: "{{product}} in another aisle from its {{block}} block",
    orphanBaysTitle: "{{product}} {{bays}} bays from its {{block}} block",
    orphanDetail: "Both neighbours are from other blocks. Move it back into its own block.",
    sizeOrderTitle: "{{block}} sizes out of order",
    sizeOrderLabelTitle: "{{block}} {{label}} out of order",
    pairsOutOfOrder_one: "1 pair out of order on the shelf",
    pairsOutOfOrder_other: "{{count}} pairs out of order on the shelf",
    outOfPlace: "out of place: {{values}}",
    biggerAbove_one: "1 place where a bigger pack sits above a smaller one",
    biggerAbove_other: "{{count}} places where a bigger pack sits above a smaller one",
    flowTitle: "{{block}} {{label}} out of flow",
    pairsOutOfFlow_one: "1 pair out of flow",
    pairsOutOfFlow_other: "{{count}} pairs out of flow",
    strayFacing: "Stray facing: {{values}}",
    ambiguousTitle: '"{{value}}" used at two levels ({{levels}})',
    ambiguousDetail:
      "Same name at two levels: {{uses}}. One tag is wrong; fix it in the product master. Nothing moves on the shelf.",
    dataGapTitle: "{{label}} missing from product data",
    dataGapDetail:
      "Sizes are in order within each {{level}} ({{values}}), but the data has no {{label}}. Fill in {{label}} on these products at source.",
    inOrderTitle: "Sizes in order within each {{level}}",
    inOrderDetail:
      "Sizes are in order inside each of {{values}}. They only look out of order read across groups. No change needed.",
    groupFirstTitle: "Group by {{level}} before {{over}} in {{scope}}",
    groupFirstDetail:
      "Sizes are in order within each {{level}} ({{values}}). The grouping places it under {{over}}, one level down; put it first to match the shelf.",
    groupFirstFix: "Group by {{label}} before {{next}} within {{node}}",
    splitByTitle: "Split {{scope}} by {{level}}",
    splitByDetail:
      "Sizes are in order within each {{level}} ({{values}}). Check whether {{node}} should be grouped by {{level}}.",
    splitByFix: "Separate {{node}} by {{label}}",
    topRow: "top",
    bottomRow: "bottom",
    rowLineTitle_one:
      '{{block}}: its rows do not give the base back; the product tagged {{attribute}} "{{value}}" forms the {{side}} row → "{{line}}" as its own row (1 SKU)',
    rowLineTitle_other:
      '{{block}}: its rows do not give the base back; the {{count}} products tagged {{attribute}} "{{value}}" form the {{side}} row → "{{line}}" as its own row ({{count}} SKUs)',
    rowLineDetail:
      'Sorted by the rows Studio reads, {{block}} does not stack the way the base does. On the base, the products tagged {{attribute}} "{{value}}" stand as the {{side}} row. A store planogram keeps "{{line}}" there as its own row.',
    rowLineFix: 'Make "{{line}}" a row of its own',
    rowQuestionTitle:
      "{{block}}: its rows do not give the base back, and no product line explains how the base stacks it",
    rowQuestionDetail:
      "Sorted by the rows Studio reads ({{order}}), {{block}} does not stack the way the base does: on the base, {{rows}}. No attribute value names the products the base stands apart. Check the item master, or tell Studio how this block is laid out.",
    rowOnShelves: "{{row}} on {{shelves}}",
    rowMissing: "products with no row",
    sizeTierLine: "{{brand}} size {{size}}",
    sizeTierTitle_one:
      "{{line}} (1 product) is tagged {{from}} but stands with the {{to}} packs on the base → treat it as {{to}}",
    sizeTierTitle_other:
      "{{line}} ({{count}} products) is tagged {{from}} but stands with the {{to}} packs on the base → treat it as {{to}}",
    sizeTierDetail:
      "On the base planogram {{line}} stands on {{where}} with {{brand}}'s {{to}} packs; {{brand}}'s other {{from}} packs stand on {{ownWhere}}. Treated as {{to}}, a store planogram keeps it in the {{to}} row.",
    sizeTierFix: "Treat {{line}} as {{to}}",
    sizeLayoutTitle:
      "{{brand}}: on the base its sizes run small to large and the sub-brands {{subBrands}} take turns shelf by shelf. How should the store planograms lay it out?",
    sizeLayoutTitleUnsorted:
      "{{brand}}: on the base the sub-brands {{subBrands}} take turns shelf by shelf. How should the store planograms lay it out?",
    sizeLayoutDetail:
      "On the base {{brand}}'s sub-brands stand neither on shelves of their own nor side by side as vertical blocks, so Studio cannot tell which layout is meant. Keep the base's product order, use horizontal blocking (each sub-brand on shelves of its own) or vertical blocking (each sub-brand its own vertical block).",
  },
  inference: {
    conventionChip: "Confirm order",
    exceptionsTitle: "Layout assumptions ({{count}})",
    exceptionChip: "Assumption",
    alertChip: "Check data",
    kind: {
      "orient-disagrees": "blocking differs",
      "no-order": "confirm order",
      "contrary-block": "blocked differently",
      "order-drift": "flow differs",
      "gradient-section": "pack heights",
      "size-unparsed": "pack size unreadable",
      "unit-mix": "mixed units",
      "pin-backwards": "order reversed",
    },
  },
  panel: {
    closeDirtyTitle: "Close the studio and lose your work?",
    shippedBlockingTitle: "CDTs finalized",
    shippedBlocking:
      "Store planograms will default to vertical blocking, using release {{releaseId}}.",
    shippedBlockingHorizontal:
      "Store planograms will default to horizontal blocking, using release {{releaseId}}.",
    closeWithoutRelease:
      "CDTs are not finalized for this planogram, so store planograms will block horizontally.",
    closeDirtyDescription:
      "Dismissals, accepted exceptions, applied fixes and tree edits made here are not saved yet. Closing discards them.",
    closeKeepEditing: "Keep editing",
    closeDiscard: "Discard and close",
    close: "Close",
    location: "Bay {{bay}} · Shelf {{shelf}}",
    previewRegenerated: "Preview the plan built from the tree",
    previewHarms:
      "The plan built from the tree would split a block or fail a check the plan passes now.",
    previewUnscored: "The plan built from the tree cannot be scored.",
    previewMatches: "The plan already matches the tree.",
    wallReading: {
      readsRows: "Brands stand as rows on this wall.",
      readsColumns: "Brands stand as columns on this wall.",
      setRows: "You set this wall to be read as rows. On the base, brands stand as columns.",
      setColumns: "You set this wall to be read as columns. On the base, brands stand as rows.",
      storeUsesRows: "Store planograms built from this release use horizontal blocking.",
      readAsRows: "Read it as rows instead",
      readAsColumns: "Read it as columns instead",
      flipHint: "(shows a preview first; changes what ships)",
      closeCall: "Close call — check this.",
      brandShelves: {
        label: "Brand shelves:",
        whereClose: "Full shelves where close",
        full: "Brands take full shelves",
        sales: "Brands sized to sales",
        help: "How store planograms give each brand its rows. Full shelves where close: a brand whose sales nearly fill a shelf gets the whole shelf. Brands take full shelves: every brand gets whole shelves, even past its sales. Brands sized to sales: each brand gets its share of sales, so two brands can share a shelf.",
      },
      detail:
        "{{attr}} vertical blocking score {{score}} (vertical from {{threshold}}), horizontal blocking score {{band}} (horizontal from {{bandThreshold}}). {{share}}% of SKUs sit in {{attr}} groups stacked across shelves (horizontal from {{shareThreshold}}%).",
    },
    /* The Blocking list leads with the few decisions Studio cannot make alone (owner,
       2026-10-10: "Tide Simply away from rest of Tide - split it into a separate brand?"),
       and folds every other row into one line. */
    questions: {
      headerNone: "Nothing to decide",
      headerOne: "1 thing to decide",
      headerMany: "{{count}} things to decide",
      /** A card's title with its place in the list: "2 · Keep the 6 shelf bands …". */
      numbered: "{{number}} · {{title}}",
      collapseCard: "Collapse this question",
      expandCard: "Expand this question",
      confirm: "Confirm",
      /* The size-tag questions fold under one line until the merchant opens them. */
      sizeTagsTitle_one: "One size tag to check.",
      sizeTagsTitle_other: "{{count}} size tags to check.",
      sizeTagsLine:
        "{{line}} is tagged {{from}} but the base shelves it with the {{to}} packs. The shelf stays as the base has it; you decide the tag.",
      sizeTagsMore: "Same for {{lines}}.",
      sizeTagsOpen: "Decide the tags",
      sizeTagsClose: "Hide the tags",
      splitTitle: "{{newBrand}} sits away from the rest of {{brand}}. Split it into its own brand?",
      splitTitleEdge:
        "{{newBrand}} stands as its own vertical block beside the rest of {{brand}}. Split it into its own brand?",
      run: "run {{number}}",
      locationJoin: " and ",
      splitWhereOne: "1 product in {{where}}. The rest of {{brand}} is in {{mainWhere}}.",
      splitWhereMany: "{{count}} products in {{where}}. The rest of {{brand}} is in {{mainWhere}}.",
      splitYes: "Yes, split it",
      splitNo: "No, keep it as {{brand}}",
      pairTitle:
        "{{first}} and {{second}} sit together, away from their brands. Make each its own brand?",
      pairWhereEachOne: "1 product each, {{where}}.",
      pairWhereEachMany: "{{count}} products each, {{where}}.",
      pairWhere: "{{firstCount}} and {{secondCount}} products, {{where}}.",
      pairYes: "Yes, split both",
      pairNo: "No, keep them",
      sizeTierTitleOne:
        "{{line}} (1 product) is tagged {{from}} but stands with the {{to}} packs on the base. Treat it as {{to}}?",
      sizeTierTitleMany:
        "{{line}} ({{count}} products) is tagged {{from}} but stands with the {{to}} packs on the base. Treat it as {{to}}?",
      sizeTierWhere:
        "On the base it is on {{where}} with the {{to}} packs. {{brand}}'s other {{from}} packs are on {{mainWhere}}.",
      sizeTierYes: "Yes, treat it as {{to}}",
      sizeTierNo: "No, keep it {{from}}",
      sizeTierDone: "{{line}} is now {{to}}",
      /* A brand block the base lays out neither as size tier rows nor as sub-brand rows (Wendy,
         2026-10-23, on Tide Unscented): one question, three answers, never decided silently. */
      sizeLayoutTitle:
        "{{brand}}: on the base its sizes run small to large and the sub-brands {{subBrands}} take turns shelf by shelf. How should the store planograms lay it out?",
      sizeLayoutTitleUnsorted:
        "{{brand}}: on the base the sub-brands {{subBrands}} take turns shelf by shelf. How should the store planograms lay it out?",
      sizeLayoutLadder: "Keep the base's product order",
      sizeLayoutHorizontal:
        "Horizontal blocking: each sub-brand on shelves of its own ({{order}}, as on the base)",
      sizeLayoutVertical: "Vertical blocking: each sub-brand its own vertical block",
      sizeLayoutAbove: " above ",
      listAnd: " and ",
      sizeLayoutDoneLadder: "{{brand}} keeps the base's product order",
      sizeLayoutDoneHorizontal:
        "{{brand}}: horizontal blocking, each sub-brand on shelves of its own",
      sizeLayoutDoneVertical: "{{brand}}: vertical blocking, each sub-brand its own vertical block",
      sizeLayoutFirst: "Answer how to lay out {{brand}} first",
      change: "Change",
      showMe: "Show me on the wall",
      splitDone: "{{newBrand}} is now its own brand",
      pairDone: "{{first}} and {{second}} are now their own brands",
      undo: "Undo",
      hiddenOne: "1 question hidden",
      hiddenMany: "{{count}} questions hidden",
      show: "Show",
    },
    tidyUps: {
      summaryOne: "1 tidy-up Studio handles itself",
      summaryMany: "{{count}} tidy-ups Studio handles itself",
      forBrand: "{{count}} for {{brand}}",
      others: "{{count}} others",
      detail: "nothing to decide",
    },
    /* The blocking exceptions Studio can fix, as one amber line above the questions. */
    exception: {
      /* What the exception is, in its own title, then what Studio would do, by its kind. */
      oneFlag: "{{title}} ({{location}}).",
      manyOutside: "{{count}} products stand outside their block.",
      manyOther: "{{count}} exceptions Studio can fix: {{titles}}.",
      doGap: "Studio can close the gap in the rules it ships",
      doOutside: "Studio can move it next to its block",
      doOther: "Studio can fix it in the rules it ships",
      doOutsideMany: "Studio can move them next to their blocks",
      doMany: "Studio can fix them in the rules it ships",
      tail: "the base planogram itself is not changed, and Undo takes it back. Finalize does not wait for this.",
      moveIt: "Move it",
      moveThemBack: "Move them back",
      fixIt: "Fix it",
      fixThem: "Fix them",
    },
    loadingAttributes: "Loading item master...",
    attributesFailed: "Could not load item-master attributes from the database.",
    loadingLengthUnit: "Loading the category's unit...",
    lengthUnitFailed: "Could not load the category's unit, so Studio cannot say lengths yet.",
    retry: "Try again",
    shipping: "Saving CDTs...",
    shipped: "Saved {{count}} products to the CDT table — tree {{treeId}}, release {{releaseId}}",
    storeRange_one:
      "1 product in your stores is not on the base. I placed it next to its closest match.",
    storeRange_other:
      "{{count}} products in your stores are not on the base. I placed each one next to its closest match.",
    storeRangeProduct: "{{name}} — next to {{neighbour}}",
    shipNoMerchflow:
      "No merchflow in context, so there is no decision tree to save to. Open the studio from a merchflow's planogram.",
    storeRulesRequired:
      "This CDT cannot be saved because its per-store measurements do not match the tree shown in CDT Studio. Fix the tree and try again.",
    shippedWithGaps:
      "Saved {{count}} products to the CDT table — tree {{treeId}}, release {{releaseId}}. {{dropped}} product(s) shipped with no placement and will not be generated.",
    orderChanged:
      "Worth a look: the saved CDT cannot hold every vertical block, horizontal block or group in the order shown here, so generated planograms will sequence some of them differently.",
    staleRules:
      "{{count}} placement rule(s) were not saved: {{rules}} is no longer a section of this tree, so the rule would clear every shelf it names.",
    shippedWithoutRules:
      "The CDT is saved, but the placement rules were not — nothing holds those products to their shelves when planograms are generated.",
    unplacedTitle: "{{count}} product(s) have a CDT but nowhere to stand on this planogram:",
    unplacedCombo: "{{cdt0}} / {{cdt1}}",
    unplacedProduct: "· {{name}} ({{productCode}})",
    unplacedConsequence:
      "Continue and the CDT is saved, but the generator will not place these {{count}} product(s) on any planogram.",
    unplacedContinue: "Finalize anyway",
    gateTitle: "Save placement rules for store planograms?",
    gateOwnsShelf: "{{variant}} stays on shelf {{shelves}}",
    gateOwnsShelves: "{{variant}} stays on shelves {{shelves}}",
    gateNoCost: "Nothing else has to move: no other product is pushed off a shelf.",
    gateEvicts:
      "{{evictions}} existing placement(s) across {{siblings}} other product group(s) would need " +
      "to move.",
    gateCarried: "{{count}} existing group(s) carried through unchanged",
    /* The title asks; this answers it. The modal's own two buttons are the whole
       vocabulary now — no saving state to narrate, no close that means something
       different from cancel. */
    gateOk: "Save placement rules",
    gateCancel: "Cancel",
    gateRefusalNoRows: "There are no restriction rows to save.",
    gateRefusalNoCategory:
      "This merchflow has no category id, so restriction endpoints cannot be used.",
    gateRefusalCannotRead:
      "Existing restrictions could not be loaded. Saving would replace the whole category.",
    gateRefusalEmptyStoreScope:
      "Some stored restriction groups have no store codes. Re-scope or delete them before saving.",
    gateRefusalGeneric: "{{detail}}",
    gateSaveFailed: "The restriction save failed: {{message}}",
  },
  moveReport: {
    scoreTitle: "This move lowers the score",
    previewScoreTitle: "This preview lowers the score",
    scoreLine: "Score {{scoreBefore}} → {{scoreAfter}}.",
    scoreStays: "Score stays {{score}}.",
    reachTitle: "Fix all can't recover from this move",
    reachLine: "Fix all reaches {{after}} from this tree; {{before}} without the move.",
    reachNote: "Fix all after this move reaches {{after}}.",
    reachUnknown: "Couldn't work out what Fix all reaches after this move.",
    discardsTitle: "This discards your fixes",
    discardsFixes_one: "This rebuilds the plan from the tree and discards the fix made.",
    discardsFixes_other:
      "This rebuilds the plan from the tree and discards the {{count}} fixes made.",
    budgetTitle: "This move costs facings",
    budgetDescription:
      "It removes {{facings}} facings ({{pct}}% of the plan) and leaves about {{inches}} in of empty shelf. The tree will be right; the shelf will be emptier.",
    budgetFillTitle: "This move costs fill",
    budgetFillDescription:
      "It keeps the facings but costs {{pts}} points of fill ({{before}}% → {{after}}%), leaving about {{inches}} in of shelf uncovered. The tree will be right; the shelf will be emptier.",
    budgetCancel: "Don't move",
    budgetProceed: "Move anyway",
    surgical: "Move executed surgically — everything not asked to change is preserved",
    replay: "Move executed by regeneration",
    mechanismSlide: "Re-ordered the block — the plan was not re-derived from the hierarchy",
    mechanismReband: "Re-stacked the tiers — the plan was not re-derived from the hierarchy",
    mechanismRemeasure:
      "Re-measured in place — the plan survived the hierarchy change; only the contract was re-read",
    mechanismRebuild: "Rebuild — the whole plan was re-derived from the hierarchy",
    mechanismBranchRebuild:
      "Branch re-laid to its new ladder — everything outside that branch stays in place",
    /* MEASURED, never claimed. Every quantity here is read off the
     * committed artifact, so the headline can never contradict the
     * concessions below it (a build shipped "Facings are unchanged"
     * above a report naming 63 re-dealt products). */
    churn: "{{moved}} of {{total}} products re-flowed · {{facings}} had facings re-dealt",
    churnNone: "every product held its exact position",
    noop: "Nothing changed — the move was already in place",
    aesthetics: "Aesthetics {{before}} → {{after}} (each plan scored against its own hierarchy)",
    fill: "Fill {{before}}% → {{after}}%",
    fillWorstRun: "· worst run {{run}}: {{before}}% → {{after}}%",
    fillOverflow: "· overflow {{length}} (counted apart from fill)",
    expectedSales: "Expected sales unchanged — no product added or removed",
    expectedSalesDelta: "Expected sales {{before}} → {{after}}",
  },
  hierarchy: {
    blockedVertical: "vertical",
    blockedHorizontal: "horizontal",
    moveLevelUp: "Move {{label}} up a level",
    moveLevelDown: "Move {{label}} down a level",
    nodeMeta: "{{skus}} · {{exceptions}}",
    nodeSkus_one: "1 SKU",
    nodeSkus_other: "{{count}} SKUs",
    nodeExceptions_one: "1 exception",
    nodeExceptions_other: "{{count}} exceptions",
    moveEarlier: "Move {{label}} earlier",
    moveLater: "Move {{label}} later",
    poolingAmbiguous: "blocking ambiguous at {{ratio}} facings per shelf — tap to answer",
    ambiguousLead: "Blocking ambiguous — the studio guessed, you decide (answers persist):",
    moveEarlierShort: "◀ Earlier",
    moveLaterShort: "Later ▶",
    alreadyFirst: "Already the first block.",
    alreadyLast: "Already the last block.",
    alreadyFirstIn: "Already the first block in {{parent}}.",
    alreadyLastIn: "Already the last block in {{parent}}.",
    stepChangesNothing: "Already in that place: the order on the shelf would stay the same.",
    showValues: "List the {{label}} values here",
    hideValues: "Hide the {{label}} values",
    showNextLevel: "Show the level below {{label}}",
    hideNextLevel: "Hide the level below {{label}}",
    liftLevelHere: "Put {{label}} above {{above}}, in this branch only",
    liftLevelShort: "↑",
    liftLevelAbove: "↑ Above {{above}}",
    liftLevelTitle: "Put {{label}} above {{above}}, in this branch only",
    rungScope: "level, within {{scope}}",
    rungDone: "Done",
    wholePlan: "the whole plan",
    siblingPosition: "{{position}} of {{total}} siblings",
    collapseNode: "Collapse",
    expandNode: "Expand",
    selectHint: "Select a node to reorder it among its siblings.",
    expandBranch: "Expand {{label}}",
    collapseBranch: "Collapse {{label}}",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    zoomFit: "Fit",
    zoomFitTitle: "Fit the whole tree in view",
    zoomLevel: "{{percent}}%",
    expandAllNodes: "Expand all nodes",
    collapseAllNodes: "Collapse all nodes",
    expandGraph: "Expand graph",
    collapseGraph: "Collapse graph",
    /* The tree folds to one line while questions are open. */
    foldTitle: "How the wall is blocked:",
    foldThen: ", then ",
    /** A level with how many blocks it holds, in the footer's words: "Brand (24 blocks)". No
     *  plural is made of a label. */
    foldSections_one: "{{label}} (1 section)",
    foldSections_other: "{{label}} ({{count}} sections)",
    foldBlocks_one: "{{label}} (1 block)",
    foldBlocks_other: "{{label}} ({{count}} blocks)",
    foldExpand: "Expand",
    foldCollapse: "Fold",
  },
};

export type CdtStudioTranslations = typeof cdtStudioEn;
